import React, { useCallback, useEffect, useMemo, useState } from 'react';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { fetchPatternScannerPageViews } from '../../api/adminPatternScannerPageViews';

const DEFAULT_FILTERS = {
  userLogin: '',
  asset: '',
  timeframe: '',
};

const PAGE_SIZE = 25;

function formatViewedAt(value) {
  if (!value) {
    return 'n/a';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('fr-FR');
}

const PatternScannerPageViewsAdminPage = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadRows = useCallback(async (nextFilters, nextPage = 1) => {
    setLoading(true);
    setError('');
    try {
      const payload = await fetchPatternScannerPageViews({
        ...nextFilters,
        page: Math.max(0, nextPage - 1),
        size: PAGE_SIZE,
      });
      const content = Array.isArray(payload?.content)
        ? payload.content
        : Array.isArray(payload)
          ? payload
          : [];
      const resolvedTotalElements = Number.isFinite(payload?.totalElements) ? payload.totalElements : content.length;
      const resolvedTotalPages = Number.isFinite(payload?.totalPages) ? payload.totalPages : (content.length > 0 ? 1 : 0);

      setRows(content);
      setTotalElements(resolvedTotalElements);
      setTotalPages(resolvedTotalPages);

      if (resolvedTotalPages === 0 && nextPage !== 1) {
        setCurrentPage(1);
      }
      if (resolvedTotalPages > 0 && nextPage > resolvedTotalPages) {
        setCurrentPage(resolvedTotalPages);
      }
    } catch (loadError) {
      setRows([]);
      setTotalElements(0);
      setTotalPages(0);
      setError(loadError.message || 'Impossible de charger l’historique Pattern Scanner.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows(appliedFilters, currentPage);
  }, [appliedFilters, currentPage, loadRows]);

  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters).filter((value) => String(value || '').trim() !== '').length,
    [appliedFilters],
  );

  const handleFilterChange = (key) => (event) => {
    setFilters((current) => ({
      ...current,
      [key]: event.target.value,
    }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    setAppliedFilters({ ...filters });
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 760 }}>
              <Chip
                icon={<HistoryRoundedIcon sx={{ color: '#f7f3ea !important' }} />}
                label="Admin Scanner Logs"
                sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }}
              />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Historique des consultations Pattern Scanner
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Cette page liste les pages du Pattern Scanner consultées par les utilisateurs connectés. Les entrées sont triées du plus récent au plus ancien.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <Chip label={`${totalElements} log(s)`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
              <Chip label={`${activeFilterCount} filtre(s) actif(s)`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fffaf2' }}>
          <Stack spacing={2.2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.5 }}>
                  Filtres
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Filtre par user login, asset ou timeframe pour retrouver rapidement les consultations récentes.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
                <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={handleApplyFilters} sx={{ bgcolor: '#17323c', '&:hover': { bgcolor: '#0f2329' } }}>
                  Rechercher
                </Button>
                <Button variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={handleResetFilters} sx={{ borderColor: '#17323c', color: '#17323c' }}>
                  Réinitialiser
                </Button>
                <Button variant="text" startIcon={<RefreshRoundedIcon />} onClick={() => loadRows(appliedFilters, currentPage)} disabled={loading} sx={{ color: '#17323c' }}>
                  Actualiser
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.2 }}>
              <TextField label="User login" size="small" value={filters.userLogin} onChange={handleFilterChange('userLogin')} />
              <TextField label="Asset" size="small" value={filters.asset} onChange={handleFilterChange('asset')} placeholder="BTC, ETH, SOL..." />
              <FormControl size="small">
                <InputLabel>Timeframe</InputLabel>
                <Select label="Timeframe" value={filters.timeframe} onChange={handleFilterChange('timeframe')}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="1H">1H</MenuItem>
                  <MenuItem value="4H">4H</MenuItem>
                  <MenuItem value="1D">1D</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff' }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.5 }}>
                Logs
              </Typography>
              <Typography variant="body2" sx={{ color: '#4f646f' }}>
                Les lignes les plus récentes sont affichées en premier.
              </Typography>
              {totalPages > 1 ? (
                <Typography variant="body2" sx={{ color: '#4f646f', mt: 0.5 }}>
                  Page {currentPage} sur {totalPages}
                </Typography>
              ) : null}
            </Box>

            {loading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress sx={{ color: '#19323c', mb: 1.5 }} />
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Chargement de l’historique...
                </Typography>
              </Box>
            ) : rows.length === 0 ? (
              <Alert severity="info">Aucune consultation Pattern Scanner ne correspond aux filtres actifs.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Date / heure</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>User login</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Asset</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Timeframe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{formatViewedAt(row.viewedAt)}</TableCell>
                        <TableCell>{row.userLogin}</TableCell>
                        <TableCell>{row.asset || 'Racine scanner'}</TableCell>
                        <TableCell>{row.timeframe}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!loading && totalPages > 1 ? (
              <Stack direction="row" justifyContent="center">
                <Pagination
                  page={Math.min(currentPage, Math.max(totalPages, 1))}
                  count={Math.max(totalPages, 1)}
                  color="primary"
                  onChange={(_, page) => setCurrentPage(page)}
                />
              </Stack>
            ) : null}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default PatternScannerPageViewsAdminPage;